import { motion } from 'framer-motion';
import {
  User,
  Phone,
  Mail,
  Save,
  Lock,
  Loader2,
  Settings,
  Eye,
  EyeOff,
  Key,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

/**
 * Personal-profile section
 * - read-only username / full-name (from Emirates ID)
 * - editable display name, email, phone
 * - change password
 */
export default function ProfileSection({
  language,
  profile,
  setProfile,
  passwords,
  setPasswords,
  showPasswords,
  setShowPasswords,
  saving,
  savingPassword,
  onSaveProfile,
  onChangePassword,
}) {
  const t = (ar, en) => (language === 'ar' ? ar : en);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6"
      data-testid="profile-section"
    >
      <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
        <User className="h-5 w-5 text-[#d4af37]" />
        {t('إدارة الحساب الشخصي', 'Personal Account Management')}
      </h2>

      {/* Profile Data */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10 flex items-center gap-2">
          <Settings className="h-4 w-4 text-[#d4af37]" />
          {t('البيانات الشخصية', 'Personal Information')}
        </h3>
        <div className="space-y-4">
          {/* Username — read-only */}
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {t('اسم المستخدم', 'Username')}
            </label>
            <div className="relative">
              <User className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                value={profile.username}
                readOnly
                className="pr-10 text-right h-12 bg-white/[0.02] border-white/10 text-[#A1A1AA] placeholder:text-[#71717A] focus:border-[#d4af37] cursor-not-allowed"
                placeholder={t('اسم المستخدم', 'Username')}
                data-testid="profile-username"
              />
            </div>
            <p className="text-xs text-[#606060] mt-1">
              {t('اسم المستخدم لا يمكن تغييره', 'Username cannot be changed')}
            </p>
          </div>

          {/* Full name — read-only (auto-filled from Emirates ID) */}
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {t('الاسم الكامل', 'Full Name')}
            </label>
            <div className="relative">
              <User className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                value={profile.fullName}
                readOnly
                className="pr-10 text-right h-12 bg-white/[0.02] border-white/10 text-[#A1A1AA] placeholder:text-[#71717A] focus:border-[#d4af37] cursor-not-allowed"
                placeholder={t('سيتم ملؤه من بطاقة الهوية الإماراتية', 'Will be filled from Emirates ID')}
                data-testid="profile-fullname"
              />
            </div>
            <p className="text-xs text-[#606060] mt-1">
              {t(
                'سيتم ملء هذا الحقل تلقائياً من بطاقة الهوية الإماراتية',
                'This field will be auto-filled from Emirates ID'
              )}
            </p>
          </div>

          {/* Display name */}
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {t('الاسم المعروض', 'Display Name')}
            </label>
            <div className="relative">
              <User className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="pr-10 text-right h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#d4af37]"
                placeholder={t('أدخل اسمك المعروض', 'Enter your display name')}
                data-testid="profile-display-name"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {t('البريد الإلكتروني', 'Email')}
            </label>
            <div className="relative">
              <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="pr-10 text-right h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#d4af37]"
                placeholder="example@email.com"
                data-testid="profile-email"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {t('رقم الهاتف', 'Phone Number')}
            </label>
            <div className="relative">
              <Phone className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className="pr-10 text-right h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#d4af37]"
                placeholder="+971 50 123 4567"
                data-testid="profile-phone"
              />
            </div>
          </div>

          <Button
            onClick={onSaveProfile}
            disabled={saving}
            className="w-full bg-[#d4af37] hover:bg-[#c9a227] text-black h-12 font-semibold"
            data-testid="btn-save-profile"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin ml-2" /> : <Save className="h-5 w-5 ml-2" />}
            {t('حفظ البيانات', 'Save Changes')}
          </Button>
        </div>
      </div>

      {/* Password Change */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10 flex items-center gap-2">
          <Key className="h-4 w-4 text-[#d4af37]" />
          {t('تغيير كلمة المرور', 'Change Password')}
        </h3>
        <div className="space-y-4">
          {/* Current password */}
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {t('كلمة المرور الحالية', 'Current Password')}
            </label>
            <div className="relative">
              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                className="pr-10 pl-10 text-right h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#d4af37]"
                placeholder={t('أدخل كلمة المرور الحالية', 'Enter current password')}
                data-testid="password-current"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
                data-testid="toggle-current-password-visibility"
              >
                {showPasswords.current ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {t('كلمة المرور الجديدة', 'New Password')}
            </label>
            <div className="relative">
              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                className="pr-10 pl-10 text-right h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#d4af37]"
                placeholder={t('أدخل كلمة المرور الجديدة', 'Enter new password')}
                data-testid="password-new"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
                data-testid="toggle-new-password-visibility"
              >
                {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Confirm new password */}
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {t('تأكيد كلمة المرور الجديدة', 'Confirm New Password')}
            </label>
            <div className="relative">
              <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="pr-10 pl-10 text-right h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-[#71717A] focus:border-[#d4af37]"
                placeholder={t('أعد إدخال كلمة المرور الجديدة', 'Confirm new password')}
                data-testid="password-confirm"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
                data-testid="toggle-confirm-password-visibility"
              >
                {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <Button
            onClick={onChangePassword}
            disabled={savingPassword}
            className="w-full bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 h-12 font-semibold"
            data-testid="btn-change-password"
          >
            {savingPassword ? <Loader2 className="h-5 w-5 animate-spin ml-2" /> : <Key className="h-5 w-5 ml-2" />}
            {t('تغيير كلمة المرور', 'Change Password')}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
